import { Dialog, Select } from '@st/theme'

import {
  ChecklistItemValidation,
  IncompleteReasonOption,
  STChecklistItem,
  STMissingReason
} from '@st/sdk'
import { Button, DialogButtons, Modal } from '@st/theme'
import { clsx } from 'clsx'
import { useState } from 'react'
import { match } from 'ts-pattern'
import { QuestionnaireDashboardState } from './st-questionnaire-module'
import { DocumentChecklistItem } from './ui'

export function SubmitClientOrganizerDialog({
  submitDialog,
  documentChecklistItems,
  nonDocumentChecklistItems,
  missingReasonOptionsByConstraintId,
  onCancel,
  onSubmit
}: {
  submitDialog: NonNullable<QuestionnaireDashboardState['submitDialog']>
  documentChecklistItems: STChecklistItem[]
  nonDocumentChecklistItems: STChecklistItem[]
  missingReasonOptionsByConstraintId: Record<string, STMissingReason[]>
  onCancel: () => void
  onSubmit: (incompleteReasonOptions: IncompleteReasonOption[]) => void
}) {
  if (submitDialog.status == 'loading') {
    return <Modal isOpen={true}>{null}</Modal>
  }

  type ChecklistItemWithValidationResult = STChecklistItem & {
    validationResult: ChecklistItemValidation | undefined
    incompleteReasonOptions: STMissingReason[]
  }

  const [incompleteReasonOptions, setIncompleteReasonOptions] = useState<
    Record<string, IncompleteReasonOption>
  >({})

  const checklistItemsWithMissingReasons: ChecklistItemWithValidationResult[] = [
    ...documentChecklistItems,
    ...nonDocumentChecklistItems
  ]
    .map((item) => {
      return {
        ...item,
        validationResult: submitDialog.validationResult.validations.find(
          (validation) => validation.checklistItemId == item.id
        ),
        incompleteReasonOptions: missingReasonOptionsByConstraintId[item.constraint.id] ?? []
      }
    })
    .filter((item) => item.validationResult?.status == 'requires_missing_reason')

  return (
    <Modal isOpen={true}>
      <Dialog
        title="Before you submit"
        subtitle={match(submitDialog.validationResult.status)
          .with(
            'missing_reasons_error',
            () =>
              `You're still missing some items we expected to receive from you. To submit without them, please let us know why below.`
          )
          .otherwise(
            () => 'Please make sure you have most of your documents uploaded before submitting.'
          )}
        className="max-w-3xl"
        buttons={
          <DialogButtons>
            <Button variant="default" onClick={onCancel}>
              Go back
            </Button>
            <Button
              variant="primary"
              disabled={submitDialog.status == 'submitting'}
              onClick={() => onSubmit(Object.values(incompleteReasonOptions))}
            >
              {submitDialog.status == 'submitting' ? 'Submitting...' : 'Submit'}
            </Button>
          </DialogButtons>
        }
      >
        <div className="flex flex-col border-t border-gray-200">
          {checklistItemsWithMissingReasons.map((item) => (
            <DocumentChecklistItem
              key={item.id}
              title={item.name}
              description={item.note}
              buttons={
                <Select
                  className={clsx('-my-1 w-48', {
                    'ring-red-500':
                      submitDialog.submissionCount > 0 &&
                      item.validationResult?.status == 'requires_missing_reason' &&
                      !incompleteReasonOptions[item.id]
                  })}
                  placeholder="Select a reason"
                  options={item.incompleteReasonOptions}
                  buildValue={(option) => option.key}
                  buildLabel={(option) => option.name}
                  value={incompleteReasonOptions[item.id]?.incompleteReason ?? undefined}
                  onChange={(value) => {
                    setIncompleteReasonOptions({
                      ...incompleteReasonOptions,
                      [item.id]: {
                        checklistItemId: item.id,
                        incompleteReason: value
                      }
                    })
                  }}
                />
              }
            />
          ))}
        </div>
      </Dialog>
    </Modal>
  )
}
